import { doc, getDoc, arrayRemove, runTransaction } from 'firebase/firestore';
import { db } from '../firebase';
import getServerTimestamp from './endpoints/GetServerTimestamp';

export const addComment = async ({ clientId, scanId, scan, comment, currentUser, markReadForOppositeRoles = false }) => {
  const scanRef = doc(db, 'clients', clientId, 'scans', scanId);
  let updatedScan = { ...scan };

  try {
    await runTransaction(db, async (transaction) => {
      const scanSnap = await getDoc(scanRef);
      if (!scanSnap.exists()) {
        throw new Error('Scan document not found');
      }
      const scanData = scanSnap.data();
      const clientRef = doc(db, 'clients', clientId);
      const clientSnap = await getDoc(clientRef);
      if (!clientSnap.exists()) {
        throw new Error('Client document not found');
      }
      const clientData = clientSnap.data();
      const practitionerId = clientData.practitionerId;
      const timestamp = await getServerTimestamp();

      const newComment = {
        ...comment,
        dateCreated: timestamp,
        createdBy: currentUser.uid,
        markAsRead: false,
      };

      // Initialize comments array if it doesn't exist
      const currentComments = scanData.comments || [];

      // Prepare updated comments array
      let updatedComments = [...currentComments, newComment];
      if (markReadForOppositeRoles) {
        updatedComments = updatedComments.map((c) =>
          c.markAsRead === false &&
          ((currentUser.role === 'practitioner' && ['admin', 'manager'].includes(c.role)) ||
           (['admin', 'manager'].includes(currentUser.role) && c.role === 'practitioner'))
            ? { ...c, markAsRead: true }
            : c
        );
      }

      // Single update to comments array
      transaction.update(scanRef, {
        comments: updatedComments,
      });

      // Update commentsToRead and add scan if necessary
      if (currentUser.role === 'practitioner') {
        const activeScansRef = doc(db, 'app', 'activeScans');
        const activeScansSnap = await getDoc(activeScansRef);
        const activeScansData = activeScansSnap.exists() ? activeScansSnap.data() : { scans: [] };
        const scanExists = activeScansData.scans.some((s) => s.id === scanId);
        const scanEntry = {
          clientId,
          date: scanData.scanDate,
          id: scanId,
          modifiedDate: timestamp,
          practitionerId,
          commentsToRead: true,
          ...(scanData.reportsUploaded && scanData.imprintMailed ? {} : { 
            scanStatus: scanData.scanPaid ? 4 : scanData.clientForm ? 3 : scanData.kitMailed ? 2 : 1 
          }),
        };

        if (!scanExists) {
          transaction.set(activeScansRef, { scans: [...activeScansData.scans, scanEntry] }, { merge: true });
        } else {
          const updatedScans = activeScansData.scans.map((s) => {
            if (s.id === scanId) {
              return { ...s, commentsToRead: true, modifiedDate: timestamp };
            }
            return s;
          });
          transaction.set(activeScansRef, { scans: updatedScans }, { merge: true });
        }
      } else if (['admin', 'manager'].includes(currentUser.role)) {
        const practitionerActiveScansRef = doc(db, 'activeScans', practitionerId);
        const practitionerActiveScansSnap = await getDoc(practitionerActiveScansRef);
        const practitionerActiveScansData = practitionerActiveScansSnap.exists()
          ? practitionerActiveScansSnap.data()
          : { scans: [] };
        const scanExists = practitionerActiveScansData.scans.some((s) => s.id === scanId);
        const scanEntry = {
          clientId,
          date: scanData.scanDate,
          id: scanId,
          modifiedDate: timestamp,
          commentsToRead: true,
          ...(scanData.reportsUploaded && scanData.imprintMailed ? {} : { 
            scanStatus: scanData.scanPaid ? 4 : scanData.clientForm ? 3 : scanData.kitMailed ? 2 : 1 
          }),
        };

        if (!scanExists) {
          transaction.set(practitionerActiveScansRef, { scans: [...practitionerActiveScansData.scans, scanEntry] }, { merge: true });
        } else {
          const updatedPractitionerScans = practitionerActiveScansData.scans.map((s) => {
            if (s.id === scanId) {
              return { ...s, commentsToRead: true, modifiedDate: timestamp };
            }
            return s;
          });
          transaction.set(practitionerActiveScansRef, { scans: updatedPractitionerScans }, { merge: true });
        }
      }

      // Update commentsToRead based on remaining unread comments
      if (markReadForOppositeRoles) {
        const isScanComplete = scanData.reportsUploaded && scanData.imprintMailed;
        await updateCommentsToRead(transaction, scanRef, scanId, practitionerId, currentUser, updatedComments, isScanComplete);
      }

      updatedScan = {
        ...scanData,
        comments: updatedComments,
      };
    });
  } catch (err) {
    console.error('Transaction error:', err);
    throw new Error(`Failed to add comment: ${err.message}`);
  }

  return updatedScan;
};

export const editComment = async ({ clientId, scanId, scan, commentId, newText, currentUser }) => {
  const scanRef = doc(db, 'clients', clientId, 'scans', scanId);
  let updatedScan = { ...scan };

  try {
    await runTransaction(db, async (transaction) => {
      const scanSnap = await getDoc(scanRef);
      if (!scanSnap.exists()) {
        throw new Error('Scan document not found');
      }
      const scanData = scanSnap.data();
      const clientRef = doc(db, 'clients', clientId);
      const clientSnap = await getDoc(clientRef);
      if (!clientSnap.exists()) {
        throw new Error('Client document not found');
      }
      const clientData = clientSnap.data();
      const practitionerId = clientData.practitionerId;
      const timestamp = await getServerTimestamp();

      const updatedComments = scanData.comments.map((c) =>
        c.id === commentId ? { ...c, text: newText, markAsRead: false } : c
      );
      transaction.update(scanRef, { comments: updatedComments });

      // Update commentsToRead and add scan if necessary
      if (currentUser.role === 'practitioner') {
        const activeScansRef = doc(db, 'app', 'activeScans');
        const activeScansSnap = await getDoc(activeScansRef);
        const activeScansData = activeScansSnap.exists() ? activeScansSnap.data() : { scans: [] };
        const scanExists = activeScansData.scans.some((s) => s.id === scanId);
        const scanEntry = {
          clientId,
          date: scanData.scanDate,
          id: scanId,
          modifiedDate: timestamp,
          practitionerId,
          commentsToRead: true,
          ...(scanData.reportsUploaded && scanData.imprintMailed ? {} : { 
            scanStatus: scanData.scanPaid ? 4 : scanData.clientForm ? 3 : scanData.kitMailed ? 2 : 1 
          }),
        };

        if (!scanExists) {
          transaction.set(activeScansRef, { scans: [...activeScansData.scans, scanEntry] }, { merge: true });
        } else {
          const updatedScans = activeScansData.scans.map((s) => {
            if (s.id === scanId) {
              return { ...s, commentsToRead: true, modifiedDate: timestamp };
            }
            return s;
          });
          transaction.set(activeScansRef, { scans: updatedScans }, { merge: true });
        }
      } else if (['admin', 'manager'].includes(currentUser.role)) {
        const practitionerActiveScansRef = doc(db, 'activeScans', practitionerId);
        const practitionerActiveScansSnap = await getDoc(practitionerActiveScansRef);
        const practitionerActiveScansData = practitionerActiveScansSnap.exists()
          ? practitionerActiveScansSnap.data()
          : { scans: [] };
        const scanExists = practitionerActiveScansData.scans.some((s) => s.id === scanId);
        const scanEntry = {
          clientId,
          date: scanData.scanDate,
          id: scanId,
          modifiedDate: timestamp,
          commentsToRead: true,
          ...(scanData.reportsUploaded && scanData.imprintMailed ? {} : { 
            scanStatus: scanData.scanPaid ? 4 : scanData.clientForm ? 3 : scanData.kitMailed ? 2 : 1 
          }),
        };

        if (!scanExists) {
          transaction.set(practitionerActiveScansRef, { scans: [...practitionerActiveScansData.scans, scanEntry] }, { merge: true });
        } else {
          const updatedPractitionerScans = practitionerActiveScansData.scans.map((s) => {
            if (s.id === scanId) {
              return { ...s, commentsToRead: true, modifiedDate: timestamp };
            }
            return s;
          });
          transaction.set(practitionerActiveScansRef, { scans: updatedPractitionerScans }, { merge: true });
        }
      }

      updatedScan = {
        ...scanData,
        comments: updatedComments,
      };
    });
  } catch (err) {
    throw new Error(`Failed to edit comment: ${err.message}`);
  }

  return updatedScan;
};

export const deleteComment = async ({ clientId, scanId, scan, comment, currentUser }) => {
  const scanRef = doc(db, 'clients', clientId, 'scans', scanId);
  let updatedScan = { ...scan };

  try {
    await runTransaction(db, async (transaction) => {
      const scanSnap = await getDoc(scanRef);
      if (!scanSnap.exists()) {
        throw new Error('Scan document not found');
      }
      const scanData = scanSnap.data();
      const clientRef = doc(db, 'clients', clientId);
      const clientSnap = await getDoc(clientRef);
      if (!clientSnap.exists()) {
        throw new Error('Client document not found');
      }
      const clientData = clientSnap.data();
      const practitionerId = clientData.practitionerId;

      transaction.update(scanRef, {
        comments: arrayRemove(comment),
      });

      const updatedComments = scanData.comments.filter((c) => c.id !== comment.id);
      const isScanComplete = scanData.reportsUploaded && scanData.imprintMailed;

      // Handle commentsToRead updates and scan removal if necessary
      if (currentUser.role === 'practitioner') {
        const hasUnreadPractitionerComments = updatedComments.some(
          (c) => c.role === 'practitioner' && c.markAsRead === false
        );
        const activeScansRef = doc(db, 'app', 'activeScans');
        const activeScansSnap = await getDoc(activeScansRef);
        const activeScansData = activeScansSnap.exists() ? activeScansSnap.data() : { scans: [] };
        const scanToUpdate = activeScansData.scans.find((s) => s.id === scanId);

        if (scanToUpdate) {
          if (isScanComplete && !hasUnreadPractitionerComments) {
            transaction.update(activeScansRef, {
              scans: arrayRemove(scanToUpdate),
            });
          } else {
            const updatedScans = activeScansData.scans.map((s) => {
              if (s.id === scanId) {
                return { ...s, commentsToRead: hasUnreadPractitionerComments };
              }
              return s;
            });
            transaction.set(activeScansRef, { scans: updatedScans }, { merge: true });
          }
        }
      } else if (['admin', 'manager'].includes(currentUser.role)) {
        const hasUnreadAdminManagerComments = updatedComments.some(
          (c) => ['admin', 'manager'].includes(c.role) && c.markAsRead === false
        );
        const practitionerActiveScansRef = doc(db, 'activeScans', practitionerId);
        const practitionerActiveScansSnap = await getDoc(practitionerActiveScansRef);
        const practitionerActiveScansData = practitionerActiveScansSnap.exists()
          ? practitionerActiveScansSnap.data()
          : { scans: [] };
        const scanToUpdate = practitionerActiveScansData.scans.find((s) => s.id === scanId);

        if (scanToUpdate) {
          if (isScanComplete && !hasUnreadAdminManagerComments) {
            transaction.update(practitionerActiveScansRef, {
              scans: arrayRemove(scanToUpdate),
            });
          } else {
            const updatedPractitionerScans = practitionerActiveScansData.scans.map((s) => {
              if (s.id === scanId) {
                return { ...s, commentsToRead: hasUnreadAdminManagerComments };
              }
              return s;
            });
            transaction.set(practitionerActiveScansRef, { scans: updatedPractitionerScans }, { merge: true });
          }
        }
      }

      updatedScan = {
        ...scanData,
        comments: updatedComments,
      };
    });
  } catch (err) {
    throw new Error(`Failed to delete comment: ${err.message}`);
  }

  return updatedScan;
};

export const markCommentsAsRead = async ({ clientId, scanId, scan, currentUser }) => {
  const scanRef = doc(db, 'clients', clientId, 'scans', scanId);
  let updatedScan = { ...scan };

  try {
    await runTransaction(db, async (transaction) => {
      const scanSnap = await getDoc(scanRef);
      if (!scanSnap.exists()) {
        throw new Error('Scan document not found');
      }
      const scanData = scanSnap.data();
      const clientRef = doc(db, 'clients', clientId);
      const clientSnap = await getDoc(clientRef);
      if (!clientSnap.exists()) {
        throw new Error('Client document not found');
      }
      const clientData = clientSnap.data();
      const practitionerId = clientData.practitionerId;

      const commentsToUpdate = scanData.comments?.filter(
        (c) =>
          c.markAsRead === false &&
          ((currentUser.role === 'practitioner' &&
            ['admin', 'manager'].includes(c.role)) ||
           (['admin', 'manager'].includes(currentUser.role) &&
             c.role === 'practitioner'))
      ) || [];

      if (commentsToUpdate.length > 0) {
        const updatedComments = scanData.comments.map((c) => {
          if (commentsToUpdate.some((uc) => uc.id === c.id)) {
            return { ...c, markAsRead: true };
          }
          return c;
        });
        transaction.update(scanRef, { comments: updatedComments });

        // Update commentsToRead and remove scan if complete
        const isScanComplete = scanData.reportsUploaded && scanData.imprintMailed;
        await updateCommentsToRead(transaction, scanRef, scanId, practitionerId, currentUser, updatedComments, isScanComplete);

        updatedScan = {
          ...scanData,
          comments: updatedComments,
        };
      }
    });
  } catch (err) {
    throw new Error(`Failed to mark comments as read: ${err.message}`);
  }

  return updatedScan;
};

// Helper function to update commentsToRead and remove scan if complete
const updateCommentsToRead = async (transaction, scanRef, scanId, practitionerId, currentUser, updatedComments, isScanComplete) => {
  if (currentUser.role === 'practitioner') {
    const hasUnreadAdminManagerComments = updatedComments.some(
      (c) => ['admin', 'manager'].includes(c.role) && c.markAsRead === false
    );
    const practitionerActiveScansRef = doc(db, 'activeScans', practitionerId);
    const practitionerActiveScansSnap = await getDoc(practitionerActiveScansRef);
    const practitionerActiveScansData = practitionerActiveScansSnap.exists()
      ? practitionerActiveScansSnap.data()
      : { scans: [] };
    const scanToUpdate = practitionerActiveScansData.scans.find((s) => s.id === scanId);

    if (scanToUpdate) {
      if (isScanComplete && !hasUnreadAdminManagerComments) {
        transaction.update(practitionerActiveScansRef, {
          scans: arrayRemove(scanToUpdate),
        });
      } else {
        const updatedPractitionerScans = practitionerActiveScansData.scans.map((s) => {
          if (s.id === scanId) {
            return { ...s, commentsToRead: hasUnreadAdminManagerComments };
          }
          return s;
        });
        transaction.set(practitionerActiveScansRef, { scans: updatedPractitionerScans }, { merge: true });
      }
    }
  } else if (['admin', 'manager'].includes(currentUser.role)) {
    const hasUnreadPractitionerComments = updatedComments.some(
      (c) => c.role === 'practitioner' && c.markAsRead === false
    );
    const activeScansRef = doc(db, 'app', 'activeScans');
    const activeScansSnap = await getDoc(activeScansRef);
    const activeScansData = activeScansSnap.exists() ? activeScansSnap.data() : { scans: [] };
    const scanToUpdate = activeScansData.scans.find((s) => s.id === scanId);

    if (scanToUpdate) {
      if (isScanComplete && !hasUnreadPractitionerComments) {
        transaction.update(activeScansRef, {
          scans: arrayRemove(scanToUpdate),
        });
      } else {
        const updatedScans = activeScansData.scans.map((s) => {
          if (s.id === scanId) {
            return { ...s, commentsToRead: hasUnreadPractitionerComments };
          }
          return s;
        });
        transaction.set(activeScansRef, { scans: updatedScans }, { merge: true });
      }
    }
  }
};